/**
 * Price formatting for the admin dual-price views (original vs with margin).
 * The API computes all adjusted figures server-side; these helpers only format.
 */

export function curr(v: number | null | undefined): string {
  if (v == null) return '—';
  return `₹${Number(v).toLocaleString('en-IN')}`;
}

/** "−5%" / "+5%" style label for a margin snapshot; em dash when unknown. */
export function marginLabel(pct: number | null | undefined, sign: '+' | '−'): string {
  if (pct == null) return '—';
  const trimmed = Number(pct) % 1 === 0 ? Number(pct).toFixed(0) : String(Number(pct));
  return `${sign}${trimmed}%`;
}

export interface DualPrice {
  /** The original figure (what the counterparty entered). */
  original: string;
  /** The margin-adjusted figure, or null when not applicable/available. */
  adjusted: string | null;
}

/**
 * Stacked-cell model: original prominent, adjusted secondary. `adjusted` is
 * null when there is nothing to show (no price, or no margin data on a
 * legacy row where both figures are identical).
 */
export function dualPrice(
  original: number | null | undefined,
  adjusted: number | null | undefined,
): DualPrice {
  if (original == null) return { original: '—', adjusted: null };
  if (adjusted == null || adjusted === original) {
    return { original: curr(original), adjusted: null };
  }
  return { original: curr(original), adjusted: curr(adjusted) };
}

/** "Partner ₹47,500" / "Partner —" for list cells before award (#288, R36). Formatting only — no maths (P3). */
export function partnerPriceLabel(v: number | null | undefined): string {
  return `Partner ${curr(v)}`;
}

/**
 * Like `curr`, but a loss reads "−₹1,200" (minus before the rupee sign) instead of "₹-1,200" (#300 profit views).
 * Formatting only: the amount is the server's (P3).
 */
export function signedCurr(v: number | null | undefined): string {
  if (v == null) return '—';
  return v < 0 ? `−${curr(Math.abs(v))}` : curr(v);
}

/** A server percentage as sent (at most 2 decimals, en-IN), e.g. "6.1 %", "−4.5 %"; em dash when unknown. */
export function pctLabel(v: number | null | undefined): string {
  if (v == null) return '—';
  const text = Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return `${v < 0 ? '−' : ''}${text} %`;
}
